'use client';

import styled from '@emotion/styled';
import React, { useRef } from 'react';
import { Global, css } from '@emotion/core';
import { IGenericPageProps } from '../../home/ui/HomePage';
import GenericPage from '../../app/pages/ui/GenericPage';
import {
	ContentWrap,
	Css,
	Responsive,
	useIntersectionObserver,
} from '../../../ui';
import { WHERE_WE_FLY_URL } from '../config/WhereWeFlyMapUrl';
import { ISiteSectionEntry } from '../../../contentful/types/ISiteSection';
import { Topbar } from '../../../components/common/top-bar/Topbar';
import { Color } from '../../../styles/Color';
import { Shadow } from '../../../styles/Shadow';
import { StickyWrapper } from '../../../components/common/top-bar/Topbar.styles';

interface IExploreDestinationsPageProps extends IGenericPageProps {
	siteSection?: ISiteSectionEntry;
}

const MOBILE_NAVBAR_HEIGHT = 80;

export default function ExploreDestinationsPage({
	page,
	siteSection,
}: IExploreDestinationsPageProps) {
	const ref = useRef<HTMLDivElement>(null);
	const intersect = useIntersectionObserver(ref, {
		threshold: 1,
	});
	const isSticky = intersect?.isIntersecting === false;

	const iframe = (
		<StyledIframe
			src={WHERE_WE_FLY_URL}
			allow="clipboard-read; clipboard-write; web-share"
			title="Interactive Flight Information"
		/>
	);

	return (
		<>
			<Global
				styles={css`
					#ResponsumChatBotIframe {
						display: none;
					}
				`}
			/>
			<ExploreDestinationsDesktopContainer>
				<GenericPage page={page} siteSection={siteSection} hideHeaderImage>
					<StyledContentWrap>
						<StyledIframeWrapper>{iframe}</StyledIframeWrapper>
					</StyledContentWrap>
				</GenericPage>
			</ExploreDestinationsDesktopContainer>

			<ExploreDestinationsMobileContainer>
				<StickyWrapper isSticky={isSticky}>
					<Topbar siteSectionId={siteSection?.fields.id} />
				</StickyWrapper>
				<StyledIframeWrapperMobile>{iframe}</StyledIframeWrapperMobile>
			</ExploreDestinationsMobileContainer>
		</>
	);
}

const ExploreDestinationsDesktopContainer = styled.div`
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: none;
	}
`;

const ExploreDestinationsMobileContainer = styled.div`
	display: none;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		position: fixed;
		display: block;
	}
`;

const StyledContentWrap = styled(ContentWrap)`
	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		margin: 0;
	}
`;

const StyledIframeWrapper = styled.div`
	height: ${Css.pxToRem(690)};
	margin-bottom: ${Css.pxToRem(80)};
	border: ${Css.pxToRem(16)} solid ${Color.Neutral1};
	border-radius: 16px;
	background-color: ${Color.Neutral1};
	box-shadow: ${Shadow.Large};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		margin-bottom: ${Css.pxToRem(48)};
	}
`;

const StyledIframe = styled.iframe`
	width: 100%;
	height: 100%;
	border: none;
	border-radius: 8px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		border-radius: 0;
		box-sizing: border-box;
	}
`;

const StyledIframeWrapperMobile = styled.div`
	width: 100vw;
	height: calc(100vh - ${MOBILE_NAVBAR_HEIGHT}px);
	position: relative;
	margin-top: ${Css.pxToRem(28)};

	@supports (height: 100dvh) {
		height: calc(100dvh - ${MOBILE_NAVBAR_HEIGHT}px);
	}
`;
